import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../http'
import { buildJsonApiParams } from '../jsonApi'
import { queryKeys } from '../queryKeys'
import { resetQueryCache } from '../queryClient'
import store from '../../store'
import {
    onSignInSuccess,
    onSignOutSuccess,
} from '../../store/auth/sessionSlice'
import { TokenError } from '../errors'

/**
 * The canonical /users/me include set used across the app. Kept identical to
 * the value previously assembled in SessionContext.updateUserData() so the
 * shape consumers receive is unchanged.
 */
export const ME_INCLUDE = [
    'roleUsers',
    'roleUsers.role',
    'roleUsers.role.permissions',
    'roleUsers.company',
    'roleUsers.company.image',
    'image',
    'companies',
    'companies.role',
    'companies.image',
    'companies.user',
    'companies.user.image',
    'referredBy',
    'referrals',
    'subscriptionTokens',
    'appSumoLicense',
    'subscriptions',
    'subscriptions.items',
    'subscriptions.items.product',
    'subscriptions.items.price',
    'activeTokens',
]

function readStoredToken() {
    try {
        const t = store.getState()?.auth?.session?.token
        if (t) return t
    } catch (_) {}
    try {
        return localStorage.getItem('token')
    } catch (_) {
        return null
    }
}

/**
 * GET /users/me. Single source of truth for "are we logged in".
 * `staleTime` is generous because the profile rarely changes mid-session.
 */
export function useCurrentUser(options = {}) {
    return useQuery({
        queryKey: queryKeys.session.me,
        queryFn: ({ signal }) =>
            api.get(
                '/users/me',
                buildJsonApiParams({ include: ME_INCLUDE }),
                { signal }
            ),
        enabled: !!readStoredToken(),
        staleTime: 5 * 60 * 1000,
        // Seed from localStorage so first render is logged-in instead of
        // flashing the sign-in screen during the /me round-trip. Marked stale
        // via initialDataUpdatedAt: 0 so React Query refetches in the
        // background and replaces the cached snapshot.
        initialData: () => {
            try {
                const cached = localStorage.getItem('user')
                if (!cached) return undefined
                const parsed = JSON.parse(cached)
                return parsed?.id ? parsed : undefined
            } catch (_) {
                return undefined
            }
        },
        initialDataUpdatedAt: 0,
        retry: (count, err) => {
            if (err instanceof TokenError) return false
            return count < 1
        },
        select: (resp) => resp?.data ?? resp,
        ...options,
    })
}

export function useIsLoggedIn() {
    const { data, isLoading, isError } = useCurrentUser()
    return {
        isLoggedIn: !!data?.id,
        isLoading,
        isError,
        user: data,
    }
}

/** POST /login */
export function useLogin() {
    const qc = useQueryClient()
    return useMutation({
        mutationKey: ['session', 'login'],
        mutationFn: (payload) => api.post('/login', {}, payload),
        onSuccess: (resp) => {
            const token = resp?.data?.token ?? resp?.token
            if (token) {
                try {
                    localStorage.setItem('token', token)
                } catch (_) {}
                store.dispatch(onSignInSuccess(token))
                qc.invalidateQueries({ queryKey: queryKeys.session.me })
            }
        },
    })
}

/**
 * POST /login/impersonate
 *
 * Returns an impersonation token for the target user. This MUST NOT touch the
 * admin's own session — the token is meant to be copied into a URL and opened
 * in a separate (incognito) tab. Writing it to localStorage / dispatching
 * onSignInSuccess here would log the admin in as the impersonated user in the
 * current tab. Callers read the token from the resolved mutation result.
 */
export function useImpersonate() {
    return useMutation({
        mutationKey: ['session', 'impersonate'],
        mutationFn: (payload) => api.post('/login/impersonate', {}, payload),
    })
}

/** POST /logout */
export function useLogout() {
    return useMutation({
        mutationKey: ['session', 'logout'],
        mutationFn: () => api.post('/logout', {}, {}),
        onSettled: () => {
            try {
                localStorage.removeItem('token')
                localStorage.removeItem('user')
            } catch (_) {}
            store.dispatch(onSignOutSuccess())
            resetQueryCache()
        },
    })
}

/** PUT /users/{id} — partial update of the current user's profile fields. */
export function useUpdateCurrentUser() {
    const qc = useQueryClient()
    return useMutation({
        mutationKey: ['session', 'updateMe'],
        mutationFn: ({ id, ...payload }) =>
            api.put(`/users/${id}`, {}, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.session.me })
            qc.invalidateQueries({ queryKey: queryKeys.users.all })
        },
    })
}

/** POST /personalAccessTokens (create API key) */
export function useCreateApiKey() {
    const qc = useQueryClient()
    return useMutation({
        mutationKey: ['session', 'createApiKey'],
        mutationFn: (payload) =>
            api.post('/personalAccessTokens', {}, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.session.me })
            qc.invalidateQueries({
                queryKey: queryKeys.personalAccessTokens.all,
            })
        },
    })
}

/** PUT /personalAccessTokens/{id} — typically used to revoke. */
export function useRevokeToken() {
    const qc = useQueryClient()
    return useMutation({
        mutationKey: ['session', 'revokeToken'],
        mutationFn: ({ id, ...payload }) =>
            api.put(`/personalAccessTokens/${id}`, {}, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.session.me })
            qc.invalidateQueries({
                queryKey: queryKeys.personalAccessTokens.all,
            })
        },
    })
}

/** GET /subscriptions/balance — credits remaining for the active company. */
export function useSubscriptionBalance(companyId, options = {}) {
    return useQuery({
        queryKey: queryKeys.session.balance(companyId),
        queryFn: ({ signal }) =>
            api.get(
                '/subscriptions/balance',
                companyId ? { company_id: companyId } : undefined,
                { signal }
            ),
        enabled: !!readStoredToken(),
        staleTime: 30_000,
        select: (resp) => resp?.data ?? resp,
        ...options,
    })
}
